import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import backImage from "../../../assets/icons/arrow-back-black.svg";
import ProgressIndicator from "./ProgressIndicator";
import SecureImage from "../../../components/common/SecureImage";
import MaleActive from "../../../assets/images/ic_male_active.png";
import FemaleActive from "../../../assets/images/ic_female_active.png";
import { logEventWithData } from "../../../app/eventSlice";
import { setStateFromPayload } from "../navigoSlice";
import { useHistory, useLocation } from "react-router";
import BackButton from "../../../components/common/BackButton";
import { NavigoFinalSectionContent } from "./navigoFinalSection";
import { IonContent, IonPage } from "@ionic/react";
import { AddressBookContent } from "../../address/newIndex";
import { useIonViewWillEnter } from "@ionic/react";
import {
  clearPricesForFamily,
  getFamilyData,
  getPriceForFamily,
} from "../../familyInfo/familySlice";
import { goBackTo, withFrom } from "../utils/navBack";
import useUnhidePageOnEnter from "../../../hooks/useUnhidePageOnEnter";
// Content carries all the logic and renders NO IonPage: this screen is
// rendered inside other pages. The default export below adds the IonPage
// for the route. One IonPage per route - a nested one overwrites the
// route's viewItem.ionPageElement via registerIonPage.
export function BookingForSectionProviderContent({
  providerData: provider,
  service: service,
  pageType,
  onBackToList,
}) {
  const navigoStore = useSelector((store) => store.navigo);
  // const location = useLocation();
  // const services = location?.state?.service;
  const dispatch = useDispatch();
  const pricesForFamily = useSelector(
    (state) => state.familyInfo.pricesForFamily,
  );
  const patientProfile = useSelector(
    (store) => store.login.user?.patientProfile,
  );
  const familyStore = useSelector((store) => store.familyInfo);
  const [finalPage, setFinalPage] = useState(false);
  const [familyMember, setFamilyMember] = useState();
  const [memberRelation, setMemberRelation] = useState(0);
  const history = useHistory();
  const routeLocation = useLocation();
  const [selfPrice, setSelfPrice] = useState(null);
  const [price, setPrice] = useState(0);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  // Which ROW is selected, kept separate from which member is being booked.
  //
  // `selectedMemberId` is the memberPatientId and must stay that way: pricing
  // is keyed by it and it is what travels downstream into the booking. But two
  // family records can share (or be missing) a memberPatientId, and a radio
  // that compares on it then lights every matching row at once. Row identity is
  // `fd.id`, which is already unique — it is the React key — so the radio
  // compares on this instead and exactly one row lights.
  //
  // Same fix as bookingForSection.js. This screen is the provider/centre-visit
  // variant and was missed when that one was fixed.
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const [addressBook, setAddressBook] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    isForFamily,
    familyMemberSelected,
    familyMemberSelectedData,
    currentServices,
    familyMembers,
    isUserSelectedFamily,
    isSmoBooking,
  } = navigoStore.ui;
  const [smoAdvanced, setSmoAdvanced] = useState(false);
  const {
    loadedFamily,
    loadedRelation,
    fetchingFamily,
    fetchingRelations,
    familyData,
    relations,
  } = familyStore;

  const RELATIONS = [
    { code: 1, value: "Father" },
    { code: 2, value: "Father-in-law" },
    { code: 3, value: "Mother" },
    { code: 4, value: "Mother-in-law" },
    { code: 5, value: "Spouse" },
    { code: 6, value: "Daughter (below 18 years)" },
    { code: 7, value: "Daughter (above 18 years)" },
    { code: 8, value: "Son (below 18 years)" },
    { code: 9, value: "Son (above 18 years)" },
    { code: 10, value: "Others" },
  ];
  useEffect(() => {
    if (patientProfile?.id && pricesForFamily?.[patientProfile.id]) {
      const price =
        pricesForFamily[patientProfile.id]?.serviceModeDetails?.[0]
          ?.discountedPrice;

      if (price !== undefined) {
        setSelfPrice(price);
      }
    }
  }, [pricesForFamily, patientProfile?.id]);

  function getRelationByCode(code) {
    return RELATIONS.find((relation) => relation.code === code) || null;
  }
  useIonViewWillEnter(async () => {
    if (!service) return;
    // SMO already has family price + eligibility from the landing; don't clear
    // or refetch here (the auto-advance above reads the existing prices).
    if (isSmoBooking) return;

    setLoading(true);

    try {
      dispatch(clearPricesForFamily());

      // 1️⃣ Get family members
      const resultAction = await dispatch(getFamilyData());
      const familyMembers = resultAction.payload?.data || [];

      const pricePromises = [];

      // 2️⃣ Self price
      const selfBody = {
        memberPatientId: patientProfile.id,
        memberRelation: 0,
        patientId: patientProfile.id,
        planId: service.planId,
        serviceId: service.serviceId,
        serviceMode: service.serviceModeDetails[0].modeName,
        // Optional; backend falls back to the plan price when absent.
        serviceProviderId: provider?.id,
      };

      pricePromises.push(dispatch(getPriceForFamily(selfBody)));

      // 3️⃣ Family prices
      familyMembers.forEach((fm) => {
        const sameRelationPIds = familyMembers
          .filter((member) => member.relation === fm.relation)
          .map((member) => member.memberPatientId);

        const body = {
          memberPatientId: fm.memberPatientId,
          memberRelation: fm.relation,
          patientId: fm.patientId,
          planId: service.planId,
          serviceId: service.serviceId,
          serviceMode: service.serviceModeDetails[0].modeName,
          sameRelationPIds,
          serviceProviderId: provider?.id,
        };

        pricePromises.push(dispatch(getPriceForFamily(body)));
      });

      // 🔥 Wait for ALL prices to finish
      await Promise.all(pricePromises);
    } catch (err) {
      console.error("Price fetch failed", err);
    } finally {
      setLoading(false);
    }
  });

  const handleAvailNow = (rowId) => {
    // Resolve by the row that was tapped. Matching on memberPatientId returned
    // the FIRST record that compared equal, so with two records sharing an id
    // this could carry the wrong one's relation into the booking.
    const matchedMember =
      rowId === -1 ? null : familyData.find((member) => member.id === rowId);
    // What goes downstream is unchanged: the memberPatientId, exactly as before.
    const familyMember =
      rowId === -1 ? -1 : matchedMember?.memberPatientId ?? selectedMemberId;

    if (matchedMember) {
      setMemberRelation(matchedMember.relation);
    }
    if (service?.serviceModeDetails[0]?.modeName != "Home sample") {
      setFinalPage(true);
    }
    setFamilyMember(familyMember);
    if (service?.serviceModeDetails[0]?.modeName === "Home sample") {
      setAddressBook(true);
    }
  };

  const getPriceForMember = (memberId) => {
    return (
      pricesForFamily?.[memberId]?.serviceModeDetails?.[0]?.discountedPrice || 0
    );
  };

  // Second Medical Opinion: the "booking for" member was already chosen on the
  // landing screen, so skip this picker entirely and go straight to slots once
  // the prices for the chosen member are available. Non-SMO flows render the
  // picker as usual.
  useEffect(() => {
    if (!isSmoBooking || smoAdvanced || loading) return;
    const isSelf = familyMemberSelected === 0;
    const priceKey = isSelf ? patientProfile?.id : familyMemberSelected;
    const priceEntry = pricesForFamily?.[priceKey];
    if (!priceEntry) return; // wait until the chosen member's price is fetched

    const memberPrice = isSelf
      ? priceEntry?.serviceModeDetails?.[0]?.discountedPrice ?? 0
      : getPriceForMember(familyMemberSelected);

    setMemberRelation(isSelf ? 0 : familyMemberSelectedData?.relation ?? 0);
    setPrice(memberPrice);
    // Mirror the picker: self is passed as -1 (bookedFor resolves to 0 via
    // familyMemberSelected===0), a member is passed as its memberPatientId.
    setFamilyMember(isSelf ? -1 : familyMemberSelected);
    setFinalPage(true);
    setSmoAdvanced(true);
  }, [
    isSmoBooking,
    smoAdvanced,
    loading,
    pricesForFamily,
    familyMemberSelected,
    familyMemberSelectedData,
    patientProfile,
  ]);

  const selfServiceDetails = pricesForFamily?.[patientProfile?.id]?.serviceModeDetails?.[0];
  const selfCanAvail = selfServiceDetails?.canAvailService;
  const selfReason = selfServiceDetails?.nonServiceAvailabilityReason;
  const isSelfDisabled = selfCanAvail === false;

  return (
    <>
      {loading && (
        <div className="booking-loader-blur">
          <div className="loader-spinner"></div>
        </div>
      )}
        <>
          {finalPage ? (
            <NavigoFinalSectionContent
              familyMember={familyMember}
              provider={provider}
              memberRelation={memberRelation}
              service={service}
              price={price}
              pageType={pageType}
              onBack={() => setFinalPage(false)}
            />
          ) : addressBook ? (
            <AddressBookContent
              familyMember={familyMember}
              booking={true}
              service={service}
              memberRelation={memberRelation}
              price={price}
            />
          ) : isSmoBooking ? (
            // SMO: booking-for was chosen on the landing; never show this
            // picker — show a loader until the auto-advance (above) fires.
            <div className="booking-loader-blur">
              <div className="loader-spinner"></div>
            </div>
          ) : (
            <div className="navigo-container-screen-2">
              <div className="navigo-container-screen-2-header">
                <img
                  src={backImage}
                  alt="icon"
                  onClick={() => {
                    if (onBackToList) {
                      onBackToList();
                    } else {
                      goBackTo(history, routeLocation, "/providerCard");
                    }
                    dispatch(
                      setStateFromPayload({
                        currScreen: 1,
                        familyMemberSelected: isForFamily
                          ? familyMemberSelected
                          : -2,
                        familyMemberSelectedData: isForFamily
                          ? familyMemberSelectedData
                          : {},
                        addressSelected: null,
                      }),
                    );
                  }}
                />
                <div className="navigo-container-screen-2-main px-4">
                  <span className="font-semibold text-base leading-7 text-lightblack">
                    {familyData.firstName}
                  </span>
                </div>
              </div>
              <div className="navigo-container-screen-2-main">
                <div className="navigo-container-screen-2-main-mid">
                  <ProgressIndicator isInput={false} />
                </div>
                <p className="navigo-container-screen-2-main-mid-2">
                  Booking for
                </p>
                <div className="navigo-container-screen-2-main-family-list">
                  <div
                    className={`navigo-container-screen-2-main-family-list-ele ${
                      isSelfDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    onClick={() => {
                      if (isSelfDisabled) return;
                      setHasUserSelected(true);
                      setSelectedRowId(-1);
                      setSelectedMemberId(-1);
                      dispatch(
                        setStateFromPayload({
                          familyMemberSelected: 0,
                          familyMemberSelectedData: { ...patientProfile },
                        }),
                      );
                      setPrice(selfPrice);
                    }}
                  >
                    <div className="text-xs font-semibold text-[#97144D] mr-2 min-w-[40px]">
                      ₹{selfPrice}
                    </div>
                    <SecureImage
                      src={patientProfile.profileImage}
                      defaultImage={
                        patientProfile.gender === 1 ? FemaleActive : MaleActive
                      }
                    />
                    <div
                      className={`navigo-container-screen-2-main-family-list-ele-mid general-disabled-color-class}`}
                    >
                      <p className="navigo-container-screen-2-main-family-list-ele-mid-top">{`${
                        patientProfile.firstName
                      } ${
                        patientProfile.lastName ? patientProfile.lastName : ""
                      }`}</p>
                      <p className="navigo-container-screen-2-main-family-list-ele-mid-bottom">
                        {patientProfile.age ? `${patientProfile.age}, ` : ""}
                        self
                      </p>
                      {isSelfDisabled && selfReason && (
                        <p className="text-xs text-red-600 mt-1">
                          {selfReason}
                        </p>
                      )}
                    </div>

                    <input
                      type="radio"
                      style={{ accentColor: "#97144D" }}
                      checked={hasUserSelected && selectedRowId === -1}
                      onChange={() => {
                        if (isSelfDisabled) return;
                        setHasUserSelected(true);
                        setSelectedRowId(-1);
                        setSelectedMemberId(-1);
                        dispatch(
                          setStateFromPayload({
                            familyMemberSelected: 0,
                            familyMemberSelectedData: { ...patientProfile },
                          }),
                        );
                        setPrice(selfPrice);
                      }}
                      disabled={isSelfDisabled}
                    />
                  </div>
                  {familyData.map((fd, index) => {
                    const serviceDetails =
                      pricesForFamily?.[fd.memberPatientId]
                        ?.serviceModeDetails?.[0];
                    const price = serviceDetails?.discountedPrice;
                    const canAvail = serviceDetails?.canAvailService;
                    const reason = serviceDetails?.nonServiceAvailabilityReason;

                    // A record with no memberPatientId cannot be priced or
                    // booked — everything downstream is keyed by it. Show it as
                    // unavailable rather than letting Continue send a null id.
                    const hasMemberId =
                      fd.memberPatientId !== null &&
                      fd.memberPatientId !== undefined &&
                      fd.memberPatientId !== "";
                    const isDisabled = canAvail === false || !hasMemberId;

                    return (
                      <div
                        key={fd.id}
                        className={`navigo-container-screen-2-main-family-list-ele ${
                          isDisabled
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        onClick={() => {
                          if (isDisabled) return;
                          setHasUserSelected(true);
                          setSelectedRowId(fd.id);
                          setSelectedMemberId(fd.memberPatientId);
                          dispatch(
                            setStateFromPayload({
                              familyMemberSelected: fd.memberPatientId,
                              familyMemberSelectedData: { ...fd },
                              isUserSelectedFamily: true,
                            }),
                          );
                          setPrice(getPriceForMember(fd.memberPatientId));
                        }}
                      >
                        <div className="text-xs font-semibold text-[#97144D] mr-2 min-w-[40px]">
                          ₹{price}
                        </div>
                        <SecureImage
                          src={fd.profilePicURL}
                          defaultImage={
                            fd.gender === 1 ? FemaleActive : MaleActive
                          }
                        />
                        <div
                          className={`navigo-container-screen-2-main-family-list-ele-mid general-disabled-color-class}`}
                        >
                          <p className="navigo-container-screen-2-main-family-list-ele-mid-top">{`${
                            fd.firstName
                          } ${fd.lastName || ""}`}</p>
                          <p className="navigo-container-screen-2-main-family-list-ele-mid-bottom">
                            {fd.age ? `${fd.age}, ` : ""}
                            {getRelationByCode(fd.relation)?.value ||
                              "Relation"}
                          </p>
                          {/* 🛑 Show reason if unavailable */}
                          {isDisabled && reason && (
                            <p className="text-xs text-red-600 mt-1">
                              {reason}
                            </p>
                          )}
                        </div>
                        <input
                          type="radio"
                          style={{ accentColor: "#97144D" }}
                          checked={hasUserSelected && selectedRowId === fd.id}
                          onChange={() => {
                            if (isDisabled) return;
                            setHasUserSelected(true);
                            setSelectedRowId(fd.id);
                            setSelectedMemberId(fd.memberPatientId);
                            dispatch(
                              setStateFromPayload({
                                familyMemberSelected: fd.memberPatientId,
                                familyMemberSelectedData: { ...fd },
                                isUserSelectedFamily: true,
                              }),
                            );
                            setPrice(getPriceForMember(fd.memberPatientId));
                          }}
                          disabled={isDisabled}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="navigo-container-screen-2-bottom">
                {
                  <p
                    onClick={() => {
                      dispatch(
                        logEventWithData({
                          action: "navigo_add_family",
                          data: { is_app_event: true },
                        }),
                      );
                      history.push({
                        pathname: "/family",
                        // Record the origin: without it the family screen's
                        // BackButton has nothing to return to and falls through
                        // to the home tab, which inside MLI's app reads as
                        // leaving the PWA entirely.
                        state: withFrom(routeLocation, {
                          isMedicine: true,
                          isPath: true,
                          path: "/navigo",
                        }),
                      });
                      // dispatch(
                      //   setStateFromPayload({
                      //     openFamily: true,
                      //     openAddFamily: true,
                      //     openFamilyProfile: false,
                      //   })
                      // );
                    }}
                  >
                    Add/Edit a family member
                  </p>
                }
                <button
                  disabled={!hasUserSelected}
                  onClick={() => handleAvailNow(selectedRowId)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </>
    </>
  );
}

export default function BookingForSectionProvider(props) {
  // Ionic can leave this page `ion-page-hidden` on re-entry, blanking it.
  const pageRef = useUnhidePageOnEnter();
  return (
    <IonPage ref={pageRef}>
      <IonContent>
        <BookingForSectionProviderContent {...props} />
      </IonContent>
    </IonPage>
  );
}
